/**
 * Sorts an array of categories hierarchically:
 * 1. Top-level categories (parent_id is null or parent not in list) are sorted alphabetically (A-Z) by display name.
 * 2. Under each top-level category, all its direct subcategories are placed immediately after it,
 *    sorted alphabetically (A-Z) by display name.
 * 3. Any orphaned subcategories (parent_id is set but parent is not found) are appended alphabetically.
 *
 * @param {Array} categories - Array of category objects
 * @param {Function} [t=(s) => s] - Optional translation function
 * @returns {Array} Hierarchically sorted categories array
 */
export function sortCategoriesHierarchically(categories = [], t = (s) => s) {
    if (!categories || !Array.isArray(categories) || categories.length === 0) {
        return [];
    }

    const cats = [...categories];
    const catMap = new Map(cats.map(c => [c.id, c]));

    // Root categories: no parent_id or parent is not in the provided categories list
    const roots = cats.filter(c => !c.parent_id || !catMap.has(c.parent_id));

    // Sort roots alphabetically by translated/display name
    roots.sort((a, b) => {
        const nameA = t(a?.name || '');
        const nameB = t(b?.name || '');
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    const result = [];
    for (const parent of roots) {
        result.push(parent);
        // Direct subcategories under this parent
        const children = cats.filter(c => c.parent_id === parent.id);
        if (children.length > 0) {
            children.sort((a, b) => {
                const nameA = t(a?.name || '');
                const nameB = t(b?.name || '');
                return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
            });
            result.push(...children);
        }
    }

    // Safety fallback for any categories missed (e.g. potential circular references)
    const includedIds = new Set(result.map(c => c.id));
    const remaining = cats.filter(c => !includedIds.has(c.id));
    if (remaining.length > 0) {
        remaining.sort((a, b) => {
            const nameA = t(a?.name || '');
            const nameB = t(b?.name || '');
            return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        });
        result.push(...remaining);
    }

    return result;
}

/**
 * Returns formatted label for a category option in dropdowns.
 * If category is a subcategory and parent is present in categories list, returns "Parent > Subcategory".
 * Otherwise returns translated category name.
 *
 * @param {Object} option - Category object
 * @param {Array} allCategories - All available categories to look up parent
 * @param {Function} [t=(s) => s] - Optional translation function
 * @returns {string} Formatted label
 */
export function getCategoryOptionLabel(option, allCategories = [], t = (s) => s) {
    if (!option || !option.name) return '';
    if (option.parent_id && Array.isArray(allCategories)) {
        const parent = allCategories.find(c => c.id === option.parent_id);
        if (parent && parent.name) {
            return `${t(parent.name)} > ${t(option.name)}`;
        }
    }
    return t(option.name);
}

/**
 * Filters categories by category_type (if provided) and sorts them hierarchically.
 *
 * @param {Array} categories - Array of category objects
 * @param {string} [categoryType] - 'debit', 'credit', or null/undefined for all
 * @param {Function} [t=(s) => s] - Optional translation function
 * @returns {Array} Filtered and hierarchically sorted categories array
 */
export function filterAndSortCategories(categories = [], categoryType = null, t = (s) => s) {
    if (!categories || !Array.isArray(categories)) {
        return [];
    }

    let filtered = categories;
    if (categoryType) {
        const targetType = String(categoryType).toLowerCase();
        filtered = categories.filter(c => (c.category_type || '').toLowerCase() === targetType);
    }

    return sortCategoriesHierarchically(filtered, t);
}

/**
 * Finds a matching category from a query string (AI-returned category name).
 * Uses a cascading matching strategy:
 *   1. Exact name + type match (case-insensitive)
 *   2. Exact name match (any type)
 *   3. Hierarchy delimiter split (e.g. "Parent > Child") — match leaf name
 *   4. Translated name match via t()
 *
 * @param {string} query - The category name string from AI
 * @param {Array} categories - All available category objects
 * @param {string} [txType] - Transaction type ('Credit' or 'Debit')
 * @param {Function} [t=(s) => s] - Translation function
 * @returns {Object|null} Matched category object or null
 */
export function findMatchingCategory(query, categories = [], txType = null, t = (s) => s) {
    if (!query || !categories || !Array.isArray(categories) || categories.length === 0) {
        return null;
    }

    const q = query.trim().toLowerCase();
    const expectedType = txType
        ? (txType === 'Credit' ? 'credit' : 'debit')
        : null;

    // 1. Exact name + type match
    if (expectedType) {
        const match = categories.find(c =>
            (c.name || '').toLowerCase() === q &&
            (c.category_type || '').toLowerCase() === expectedType
        );
        if (match) return match;
    }

    // 2. Exact name match (any type)
    const exactMatch = categories.find(c => (c.name || '').toLowerCase() === q);
    if (exactMatch) return exactMatch;

    // 3. Hierarchy delimiter split — match leaf name
    if (q.includes('>')) {
        const leaf = q.split('>').pop().trim();
        if (expectedType) {
            const leafTypeMatch = categories.find(c =>
                (c.name || '').toLowerCase() === leaf &&
                (c.category_type || '').toLowerCase() === expectedType
            );
            if (leafTypeMatch) return leafTypeMatch;
        }
        const leafMatch = categories.find(c => (c.name || '').toLowerCase() === leaf);
        if (leafMatch) return leafMatch;
    }

    // 4. Translated name match
    if (t !== ((s) => s)) {
        if (expectedType) {
            const tMatch = categories.find(c =>
                t(c.name || '').toLowerCase() === q &&
                (c.category_type || '').toLowerCase() === expectedType
            );
            if (tMatch) return tMatch;
        }
        const tAnyMatch = categories.find(c => t(c.name || '').toLowerCase() === q);
        if (tAnyMatch) return tAnyMatch;
    }

    return null;
}
